'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isTopLevel } from './nav-items';
import { MobileHeaderSlot } from './mobile-header';

interface PageHeaderProps {
  title: string;
  /** A short count or scope after the title, e.g. "23 today" or "9 packs". */
  count?: ReactNode;
  /**
   * The page's one primary action, the dark button (docs/design/ui-rules.md). On a phone page the
   * top bar names, it is a round ＋ at the top bar's right end, keeping its label for screen readers.
   */
  primary?: { label: string; icon: LucideIcon; onSelect: () => void };
  /** Any other actions, as outline buttons. */
  actions?: ReactNode;
  className?: string;
}

// The top of every redesigned page: one h1, an optional count and the page's actions.
// The back link for nested pages comes from the dashboard layout, above this row.
export function PageHeader({ title, count, primary, actions, className }: PageHeaderProps) {
  // On a phone the 56px header already names a top-level page, so its h1 is only for screen readers.
  const named = isTopLevel(usePathname());
  // There its count and primary action move into that header too (docs/design/ui-rules.md, Tables),
  // so with no other actions the row takes no space on a phone.
  const empty = named && !actions;
  const Icon = primary?.icon;
  return (
    <div className={cn('mb-4 flex min-h-9 flex-wrap items-center gap-x-3 gap-y-2', empty && 'max-md:mb-0 max-md:min-h-0', className)}>
      <h1 className={cn('text-xl font-semibold leading-7 tracking-[-0.01em]', named && 'max-md:sr-only')}>{title}</h1>
      {count != null ? <span className={cn('text-sm text-muted-foreground', named && 'max-md:hidden')}>{count}</span> : null}
      {named && count != null ? <MobileHeaderSlot><span className="min-w-0 truncate text-sm text-muted-foreground md:hidden">{count}</span></MobileHeaderSlot> : null}
      {actions || primary ? (
        <div className={cn('ml-auto flex max-w-full flex-wrap items-center justify-end gap-2', !actions && named && 'max-md:hidden')}>
          {actions}
          {primary && Icon ? <Button onClick={primary.onSelect} className={cn(named && 'max-md:hidden')}><Icon aria-hidden="true" />{primary.label}</Button> : null}
        </div>
      ) : null}
      {named && primary && Icon ? (
        <MobileHeaderSlot action>
          <Button size="icon" className="size-10 md:hidden" onClick={primary.onSelect} aria-label={primary.label} title={primary.label}>
            <Icon className="size-5" aria-hidden="true" />
          </Button>
        </MobileHeaderSlot>
      ) : null}
    </div>
  );
}
